import { useCallback } from 'react'
import {
  IInvoice,
  IOrder,
  IPopularSearchAttributesValue,
  IProductGroupSearchItem,
  ISearchAttribute,
  ISearchResultAccount,
  RecentlyOpenedEntity,
  RecentlyOpenedEntityType,
  ResultObjectType
} from '@cbgms/api'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { queryParamFromVehicleSearchText } from 'app/vehicles/components/UnknownVehicleV2/query-params'
import { SearchResultTab, dashboardSearchResults } from 'routes/dashboard/routes'
import { useOpenOrderForPart } from 'app/search/hooks/useOpenOrderForPart'
import { useGotoProductGroup } from 'app/products/components/ProductGroups/useGotoProductGroup'
import { useOpenOrderForPartInGroup } from 'app/search/hooks/useOpenOrderForPartInGroup'
import { archiveOrderHistoryBaseRouteBuilder, useWorkorderBaseRoute } from 'utils/Subscription/pathAndRouteSelector'
import { ITopSearchFormatResults } from 'app/menu/components/Search/hooks/useMappedSearchResults'
import { generatePath, useHistory } from 'react-router-dom'
import { OrderQuotationDetailUrl, WorkOrderQuotationDetailUrl } from 'app/workorder/RouteConstants'
import { VehicleSalesQuotationsDetailURL } from 'app/vehicleSales/RouteConstants'
import { useTyresProductGroupID } from 'app/products/hooks/use-is-tyres-orderaccount'
import { useOpenOrderForVehicle } from 'app/search/hooks/useOpenOrderForVehicle'
import { SEARCH } from 'app/search/hooks/useQueryResultTypes'
import { useOpenVehicle } from 'app/search/hooks/useOpenVehicle'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { TopSearchResultType, VirtualSearchResultType } from '@cbgms/components/src/top-search/search-v1/Results/TopSearchResultType'
import { ProductSearchResultType } from '@cbgms/components/src/product-search/types/IProductSearchResultProps'
import { usePutNewRecentlyOpenedEntities } from 'app/search/hooks/use-fetch-recently-opened-entities'
import { splitRegistration } from '@cbgms/base/utils/vehicle'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { IProductSearchFormatResults } from 'app/order/hooks/use-product-mapped-search-results'
import { PRODUCT_SEARCH } from 'app/order/hooks/use-product-search'

type BaseAction = () => Promise<void>
type PrimaryActionBuilder = (
  resultType: TopSearchResultType | ProductSearchResultType,
  query: string,
  meta: ITopSearchFormatResults | RecentlyOpenedEntity | IProductSearchFormatResults | null
) => BaseAction
interface UseBuilderProps {
  clearQuery: () => void
  setQuery?: (value: string) => void
  refetchSearch?: () => void
  inputRef?: React.RefObject<HTMLInputElement>
  onDrawerSearchQuery?: (query: string) => void
  onDrawerProductGroupUUID?: (productGroupUUID: string) => void
  onDrawerTyreSize?: (tyreGroupUUID: string, searchAttributes: ISearchAttribute[]) => void
}
export const usePrimaryActionBuilder = (props: UseBuilderProps): PrimaryActionBuilder => {
  const vehicleActionBuilder = useVehicleActionBuilder(props)
  const accountActionBuilder = useAccountActionBuilder(props)
  const invoiceActionBuilder = useInvoiceActionBuilder(props)
  const quotationActionBuilder = useQuotationActionBuilder(props)
  const supplierOrderActionBuilder = useSupplierOrderActionBuilder(props)
  const tyreSizeSuggestionActionBuilder = useTyreSizeSuggestionActionBuilder(props)
  const productGroupActionBuilder = useProductGroupActionBuilder(props)
  const workorderActionBuilder = useWorkorderActionBuilder(props)
  const recentlyOpenedEntityActionBuilder = useRecentlyOpenedEntityActionBuilder(props)
  const callToActionCreateVehicleManuallyActionBuilder = useCallToACtionCreateVehicleManuallyActionBuilder(props)
  const callToActionSearchPartsActionBuilder = useCallToActionSearchPartsActionBuilder(props)
  const callToActionViewMoreSupplierOrdersActionBuilder = useCallToActionViewMoreSupplierOrdersActionBuilder(props)

  return (
    resultType: TopSearchResultType | ProductSearchResultType,
    query: string,
    meta: ITopSearchFormatResults | RecentlyOpenedEntity | IProductSearchFormatResults | null
  ) => {
    const topSearchResultMeta = meta as ITopSearchFormatResults
    const recentlyOpenendEntityMeta = meta as RecentlyOpenedEntity
    switch (resultType) {
      case ResultObjectType.Invoice:
        return invoiceActionBuilder((meta as ITopSearchFormatResults)!.WorkorderInvoice!)
      case ResultObjectType.VehicleWithAccount:
      case ResultObjectType.GenericVehicle:
      case ResultObjectType.DatabaseVehicle:
        return vehicleActionBuilder(topSearchResultMeta!)
      case ResultObjectType.Account:
        return accountActionBuilder(topSearchResultMeta!.Account!)
      case ResultObjectType.WorkorderQuotation:
      case ResultObjectType.VehicleSalesQuotation:
        return quotationActionBuilder(topSearchResultMeta!)
      case ResultObjectType.Order:
        return supplierOrderActionBuilder(topSearchResultMeta!.Order!)
      case ResultObjectType.WorkOrder:
      case ResultObjectType.WorkOrderArchive:
        return workorderActionBuilder(topSearchResultMeta!)
      case ResultObjectType.TyreSize:
        return tyreSizeSuggestionActionBuilder(topSearchResultMeta!.TyreSize!)
      case ResultObjectType.ProductGroup: {
        const productSearchMeta = meta as IProductSearchFormatResults
        if (!productSearchMeta?.ProductGroup) {
          return async () => {}
        }
        return productGroupActionBuilder(productSearchMeta.ProductGroup)
      }
      case VirtualSearchResultType.CallToActionViewMoreSupplierOrder:
        return callToActionViewMoreSupplierOrdersActionBuilder(query)
      case VirtualSearchResultType.CallToActionSearchParts:
        return callToActionSearchPartsActionBuilder(query)
      case VirtualSearchResultType.CallToActionCreateVehicleManually:
        return callToActionCreateVehicleManuallyActionBuilder(query)
      case ResultObjectType.RecentlyOpened:
        return recentlyOpenedEntityActionBuilder(recentlyOpenendEntityMeta)
      case VirtualSearchResultType.NoResults:
        return async () => {
          // no results: do nothing
        }

      case ResultObjectType.Error:
      case ResultObjectType.Parts: //  parts result not implemented. handled differently
      default:
        return async () => {
          console.warn('no primary action configured for:', resultType, meta)
        }
    }
  }
}

const useInvalidateQuerySearch = (productGroupOrderSearch?: boolean) => {
  const queryClient = useQueryClient()
  return useCallback(() => {
    if (productGroupOrderSearch) {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_SEARCH] })
    } else {
      queryClient.invalidateQueries({ queryKey: [SEARCH] })
    }
  }, [queryClient, productGroupOrderSearch])
}

const useAccountActionBuilder = (props: UseBuilderProps): ((account: ISearchResultAccount) => BaseAction) => {
  const history = useHistory()
  const invalidateSearch = useInvalidateQuerySearch()
  return (account: ISearchResultAccount) => async () => {
    history.push(`/accounts/${account.UUID}`)
    invalidateSearch()
    props.clearQuery()
  }
}

const useVehicleActionBuilder = (props: UseBuilderProps): ((vehicleResult: ITopSearchFormatResults) => BaseAction) => {
  const isPortal = useIsPortal()
  const invalidateSearch = useInvalidateQuerySearch()
  const openOrderForVehicle = useOpenOrderForVehicle()
  const putRecentlyOpenedEntity = usePutNewRecentlyOpenedEntities()
  const openVehicle = useOpenVehicle()

  return (vehicleResult: ITopSearchFormatResults) => async () => {
    if (isPortal) {
      const vehicleUUID = vehicleResult?.Vehicle?.Values[0].UUID
      if (vehicleUUID !== EmptyUUID && vehicleUUID) {
        await putRecentlyOpenedEntity(RecentlyOpenedEntityType.Vehicle, vehicleUUID)
      }
      await openOrderForVehicle(vehicleResult.Vehicle!)
    } else {
      await openVehicle(vehicleResult.Vehicle!, vehicleResult.Type as ResultObjectType)
    }
    invalidateSearch()
    props.clearQuery()
  }
}

const useCallToACtionCreateVehicleManuallyActionBuilder = (props: UseBuilderProps): ((query: string) => BaseAction) => {
  const history = useHistory()
  const isPortal = useIsPortal()
  const invalidateSearch = useInvalidateQuerySearch()

  return (query: string) => async () => {
    const gmsNewVehicleUrl = `/vehicles/new?${queryParamFromVehicleSearchText(query)}`
    const portalNewVehicle = `/vehicles/quick-create?${queryParamFromVehicleSearchText(query)}`
    history.push(isPortal ? portalNewVehicle : gmsNewVehicleUrl)
    invalidateSearch()
    props.clearQuery()
  }
}

const useCallToActionSearchPartsActionBuilder = (props: UseBuilderProps): ((query: string) => BaseAction) => {
  const OpenOrderForPart = useOpenOrderForPart()
  return (query: string) => async () => {
    if (props.onDrawerSearchQuery) {
      props.onDrawerSearchQuery(query)
      props.clearQuery()
      return
    }
    await OpenOrderForPart(query)
    props.clearQuery()
  }
}

const useCallToActionViewMoreSupplierOrdersActionBuilder = (props: UseBuilderProps): ((query: string) => BaseAction) => {
  const history = useHistory()
  return (query: string) => async () => {
    history.push({
      pathname: dashboardSearchResults,
      search: `q=${query}`,
      hash: SearchResultTab.orders
    })
    props.clearQuery()
  }
}

const useInvoiceActionBuilder = (props: UseBuilderProps): ((invoice: Partial<IInvoice>) => BaseAction) => {
  const history = useHistory()
  const invalidateSearch = useInvalidateQuerySearch()
  return (invoice: Partial<IInvoice>) => async () => {
    history.push(`/history/invoices/${invoice.WorkorderInvoiceUUID}`)
    invalidateSearch()
    props.clearQuery()
  }
}

const useSupplierOrderActionBuilder = (props: UseBuilderProps): ((order: IOrder) => BaseAction) => {
  const isPortal = useIsPortal()
  const history = useHistory()
  const invalidateSearch = useInvalidateQuerySearch()
  return (order: IOrder) => async () => {
    history.push(archiveOrderHistoryBaseRouteBuilder(isPortal, order))
    invalidateSearch()
    props.clearQuery()
  }
}

const useQuotationActionBuilder = (props: UseBuilderProps): ((quoteResult: ITopSearchFormatResults) => BaseAction) => {
  const history = useHistory()
  const isPortal = useIsPortal()
  const invalidateSearch = useInvalidateQuerySearch()

  return (quoteResult: ITopSearchFormatResults) => async () => {
    if (quoteResult.Type === ResultObjectType.WorkorderQuotation) {
      if (isPortal) {
        history.push(generatePath(OrderQuotationDetailUrl, { QuotationUUID: quoteResult.WorkorderQuotation!.WorkorderQuotationUUID }))
      } else {
        history.push(generatePath(WorkOrderQuotationDetailUrl, { QuotationUUID: quoteResult.WorkorderQuotation!.WorkorderQuotationUUID }))
      }
    } else {
      history.push(generatePath(VehicleSalesQuotationsDetailURL, { QuotationUUID: quoteResult.VehicleSalesQuotation!.UUID }))
    }
    invalidateSearch()
    props.clearQuery()
  }
}

const useTyreSizeSuggestionActionBuilder = (props: UseBuilderProps): ((tireResult: IPopularSearchAttributesValue) => BaseAction) => {
  const tyreGroup = useTyresProductGroupID()
  const openTyres = useOpenOrderForPartInGroup(tyreGroup, undefined)
  const invalidateSearch = useInvalidateQuerySearch()

  return (tireResult: IPopularSearchAttributesValue) => async () => {
    if (props.onDrawerTyreSize) {
      props.onDrawerTyreSize(tyreGroup, tireResult.Attributes || [])
      props.clearQuery()
      return
    }
    openTyres({ searchAttributes: tireResult.Attributes, preselectedFilters: tireResult.PreselectedFilters })
    invalidateSearch()
    props.clearQuery()
  }
}

export const useProductGroupActionBuilder = (props: UseBuilderProps) => {
  const gotoProductGroup = useGotoProductGroup()
  const invalidateSearch = useInvalidateQuerySearch(true)

  return (productGroup: IProductGroupSearchItem) => async () => {
    if (props.onDrawerProductGroupUUID) {
      props.onDrawerProductGroupUUID(productGroup.ID)
      props.clearQuery()
      return
    }
    const mockEvent = {} as React.SyntheticEvent
    gotoProductGroup(mockEvent, { ID: productGroup.ID })
    invalidateSearch()
    props.clearQuery()
  }
}

const useWorkorderActionBuilder = (props: UseBuilderProps): ((workorderResult: ITopSearchFormatResults) => BaseAction) => {
  const history = useHistory()
  const workorderPath = useWorkorderBaseRoute()
  const invalidateSearch = useInvalidateQuerySearch()

  return (workorderResult: ITopSearchFormatResults) => async () => {
    if (workorderResult.Type === ResultObjectType.WorkOrderArchive) {
      history.push(`/archive/${workorderResult.Workorder!.UUID}`)
    } else if (workorderResult.Type === ResultObjectType.WorkOrder) {
      history.push(`${workorderPath}${workorderResult.Workorder!.UUID}`)
    }
    invalidateSearch()
    props.clearQuery()
  }
}

const useRecentlyOpenedEntityActionBuilder = (props: UseBuilderProps): ((entity: RecentlyOpenedEntity) => BaseAction) => {
  const history = useHistory()
  const workorderPath = useWorkorderBaseRoute()
  const isPortal = useIsPortal()

  if (isPortal) {
    return (entity: RecentlyOpenedEntity) => async () => {
      const { licensePlate } = splitRegistration(entity.Vehicle?.Registration || '')
      if (entity.EntityType === RecentlyOpenedEntityType.Vehicle && (entity.Vehicle?.Registration || entity.Vehicle?.Vin)) {
        const vinNumberSearch = entity.Vehicle.Vin
        if (licensePlate || vinNumberSearch) {
          licensePlate ? props.setQuery?.(licensePlate) : props.setQuery?.(vinNumberSearch)
        }

        props.refetchSearch?.()
        props.inputRef?.current?.focus()
      } else {
        history.push(`/vehicles/${entity.Vehicle?.VehicleUUID}`)
        props.clearQuery()
      }
    }
  } else {
    return (entity: RecentlyOpenedEntity) => async () => {
      let url = `${workorderPath}${entity.EntityUUID}`
      if (entity.WorkorderInvoice) {
        url = generatePath('/history/invoices/:InvoiceUUID', { InvoiceUUID: entity.WorkorderInvoice.WorkorderInvoiceUUID })
      }
      history.push(url)
      props.clearQuery()
    }
  }
}
